import { useState, useEffect } from 'react'
import { useTheme } from '../../../contexts/ThemeContext'
import {
  Crew,
  RailCar,
  Track,
  PlannedCut,
  SwitchList,
  ValidationError,
  InterchangeRailCar,
  getActiveCrews,
  calculateSwitchInstructions,
  SwitchListPreview,
} from '@nyos/shared'
import { AlertTriangle, Film } from 'lucide-react'
import SwitchListPreviewMapModal from '../../../components/SwitchListPreviewMapModal'

interface DispatchGatewayModalProps {
  isOpen: boolean
  onClose: () => void
  /** Facility scope for the animated preview's geo/inventory queries. */
  facilityId?: string
  sourceTrackId: string
  targetTrackId: string
  selectedLocomotiveId: string
  plannedCuts: PlannedCut[]
  onConfirm: (crewId: string) => void
  sourceTrackName?: string
  targetTrackName?: string
  locomotiveReportingMark?: string
  availableTracks?: Track[]
  availableLocomotives?: RailCar[]
  railCars?: InterchangeRailCar[]
}

export default function DispatchGatewayModal({
  isOpen,
  onClose,
  facilityId,
  sourceTrackId,
  targetTrackId,
  selectedLocomotiveId,
  plannedCuts,
  onConfirm,
  sourceTrackName,
  targetTrackName,
  locomotiveReportingMark,
  availableTracks,
  availableLocomotives,
  railCars
}: DispatchGatewayModalProps) {
  const { theme } = useTheme()
  const [selectedCrew, setSelectedCrew] = useState<string>('')
  const [isDispatching, setIsDispatching] = useState(false)
  const [crews, setCrews] = useState<Crew[]>([])
  const [isLoadingCrews, setIsLoadingCrews] = useState(false)
  const [switchList, setSwitchList] = useState<SwitchList | null>(null)
  const [isLoadingSwitchInstructions, setIsLoadingSwitchInstructions] = useState(false)
  const [generationErrors, setGenerationErrors] = useState<ValidationError[]>([])
  const [dispatchSubmissionErrors, setDispatchSubmissionErrors] = useState<ValidationError[]>([])
  const [crewSelectError, setCrewSelectError] = useState<string | null>(null)
  const [animateOpen, setAnimateOpen] = useState(false)

  const bannerThemeStyles = {
    daylight: "bg-amber-50 border border-amber-300 text-amber-900",
    foundry: "bg-orange-950/30 border border-orange-500/40 text-orange-200",
    midnight: "bg-transparent border border-amber-700/60 text-amber-100",
    cyber: "bg-zinc-950 border border-amber-400/30 text-amber-200 tracking-wide"
  }

  const iconThemeStyles = {
    daylight: "text-amber-600",
    foundry: "text-orange-400",
    midnight: "text-amber-500",
    cyber: "text-amber-400"
  }

  const modalBackgroundStyles = {
    daylight: "bg-background",
    foundry: "bg-background",
    midnight: "bg-zinc-900/95",
    cyber: "bg-zinc-900/95"
  }

  const dispatchErrorStyles = {
    daylight: "bg-red-50 border border-red-300 text-red-900",
    foundry: "bg-red-950/30 border border-red-500/40 text-red-200",
    midnight: "bg-transparent border border-red-700/60 text-red-100",
    cyber: "bg-zinc-950 border border-red-400/30 text-red-200 tracking-wide"
  }

  const dispatchErrorIconStyles = {
    daylight: "text-red-600",
    foundry: "text-red-400",
    midnight: "text-red-500",
    cyber: "text-red-400"
  }

  useEffect(() => {
    if (isOpen) {
      setCrewSelectError(null)
      fetchActiveCrews()
      fetchSwitchInstructions()
    }
  }, [isOpen])

  // Preview state must not survive a gateway close — otherwise the next
  // open would resurrect the preview modal on top of the fresh gateway.
  useEffect(() => {
    if (!isOpen) setAnimateOpen(false)
  }, [isOpen])

  const fetchActiveCrews = async () => {
    setIsLoadingCrews(true)
    try {
      const data = await getActiveCrews()
      setCrews(data)
    } catch (error) {
      console.error('Failed to fetch crews:', error)
    } finally {
      setIsLoadingCrews(false)
    }
  }

  const fetchSwitchInstructions = async () => {
    setIsLoadingSwitchInstructions(true)
    setGenerationErrors([])
    setDispatchSubmissionErrors([])
    setSwitchList(null)
    try {
      const data = await calculateSwitchInstructions({
        sourceTrackId,
        targetTrackId,
        locomotiveId: selectedLocomotiveId,
        plannedCuts
      })
      setSwitchList(data)
    } catch (error) {
      console.error('Failed to fetch switch instructions:', error)
      setSwitchList(null)
      setGenerationErrors([{
        id: `error-${Date.now()}`,
        severity: 'ERROR',
        message: 'Failed to fetch switch list preview',
        timestamp: new Date().toISOString()
      }])
    } finally {
      setIsLoadingSwitchInstructions(false)
    }
  }

  if (!isOpen) return null

  const handleConfirm = async () => {
    if (!selectedCrew) {
      setCrewSelectError('Select a crew to dispatch this work order.')
      return
    }

    setIsDispatching(true)
    setDispatchSubmissionErrors([])
    try {
      await onConfirm(selectedCrew)
      onClose()
    } catch (error) {
      console.error('Dispatch failed:', error)
      
      // Check if it's a validation error from the API
      if (error instanceof Error && error.name === 'ValidationError' && (error as any).validationErrors) {
        setDispatchSubmissionErrors((error as any).validationErrors)
      } else {
        setDispatchSubmissionErrors([{
          id: `error-${Date.now()}`,
          severity: 'ERROR',
          message: error instanceof Error ? error.message : 'Failed to dispatch work order',
          timestamp: new Date().toISOString()
        }])
      }
    } finally {
      setIsDispatching(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className={`${modalBackgroundStyles[theme] || modalBackgroundStyles.foundry} border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-foreground">Dispatch Gateway</h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors pointer-coarse:inline-flex pointer-coarse:items-center pointer-coarse:justify-center pointer-coarse:min-h-11 pointer-coarse:min-w-11"
            >
              ✕
            </button>
          </div>

          {/* Stage 1: Generation Errors Banner (Warning) */}
          {generationErrors.length > 0 && (
            <div className={`${bannerThemeStyles[theme] || bannerThemeStyles.foundry} p-4 rounded-md shadow-sm flex items-start gap-3 mb-6`}>
              <AlertTriangle className={`${iconThemeStyles[theme] || iconThemeStyles.foundry} h-5 w-5 shrink-0 mt-0.5`} />
              <div className="flex-1">
                <h3 className="font-semibold mb-2">
                  Switch List Generation Failed
                </h3>
                <p className="text-sm mb-3 opacity-80">
                  The following validation errors prevent dispatch. Please correct the track selection and try again.
                </p>
                <ul className="space-y-1 text-sm opacity-80">
                  {generationErrors.map((error) => (
                    <li key={error.id} className="flex items-start gap-2">
                      <span className="opacity-70">•</span>
                      <span>{error.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Switch List Preview - Only show if no generation errors */}
          {generationErrors.length === 0 && (
            <div className="mb-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-muted-foreground">Switch List Preview</h3>
                <button
                  type="button"
                  onClick={() => setAnimateOpen(true)}
                  disabled={!switchList || isLoadingSwitchInstructions || !facilityId}
                  className="px-3 py-1.5 pointer-coarse:min-h-11 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
                  title={facilityId ? 'Animate this switch list on the yard map' : 'Facility scope unavailable — cannot animate'}
                >
                  <Film size={13} /> Animate
                </button>
              </div>
              <SwitchListPreview switchList={switchList} loading={isLoadingSwitchInstructions} />
            </div>
          )}

          {/* Crew Selection */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">Select Crew</h3>
            <select
              value={selectedCrew}
              onChange={(e) => {
                setSelectedCrew(e.target.value)
                setCrewSelectError(null)
              }}
              disabled={isLoadingCrews}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground disabled:opacity-50"
            >
              <option value="">-- Select a crew --</option>
              {crews.map((crew) => (
                <option key={crew.crewId} value={crew.crewId}>
                  {crew.crewName} (Cond: {crew.conductorName} / Eng: {crew.engineerName})
                </option>
              ))}
            </select>
            {isLoadingCrews && (
              <p className="text-xs text-muted-foreground mt-1">Loading active crews...</p>
            )}
            {crewSelectError && (
              <div className={`${dispatchErrorStyles[theme] || dispatchErrorStyles.foundry} border rounded-md px-3 py-2 mt-2`}>
                <p className="text-sm font-medium">{crewSelectError}</p>
              </div>
            )}
            
            {/* Stage 2: Dispatch Submission Errors (Red Inline) */}
            {dispatchSubmissionErrors.length > 0 && (
              <div className={`${dispatchErrorStyles[theme] || dispatchErrorStyles.foundry} border rounded-md p-3 mt-2`}>
                <div className="flex items-start gap-2">
                  <div className={`${dispatchErrorIconStyles[theme] || dispatchErrorIconStyles.foundry} text-sm`}>❌</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      Dispatch Failed
                    </p>
                    <ul className="mt-1 space-y-1 text-sm opacity-80">
                      {dispatchSubmissionErrors.map((error) => (
                        <li key={error.id} className="flex items-start gap-2">
                          <span className="opacity-70">•</span>
                          <span>{error.message}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Summary Information */}
          <div className="mb-6 bg-muted/30 border border-border rounded-md p-4">
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">Work Order Summary</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Cars:</span>
                <span className="text-foreground">{plannedCuts.reduce((sum, cut) => sum + cut.carIds.length, 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Passes Completed:</span>
                <span className="text-foreground">{plannedCuts.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Source Track:</span>
                <span className="text-foreground">{sourceTrackName || sourceTrackId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Final Destination:</span>
                <span className="text-foreground">{targetTrackName || targetTrackId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Locomotive:</span>
                <span className="text-foreground">{locomotiveReportingMark || selectedLocomotiveId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Crew:</span>
                <span className="text-foreground">
                  {selectedCrew
                    ? (() => {
                        const crew = crews.find(c => c.crewId === selectedCrew)
                        return crew
                          ? `${crew.crewName} (Cond: ${crew.conductorName} / Eng: ${crew.engineerName})`
                          : selectedCrew
                      })()
                    : 'Not selected'
                  }
                </span>
              </div>
            </div>

            {/* Pass Sequences Breakdown */}
            {plannedCuts.length > 0 && (
              <div className="mt-4 space-y-2 border-t pt-4">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Pass Blueprint</h4>
                <div className="max-h-[180px] overflow-y-auto space-y-2 pr-1">
                  {plannedCuts.map((cut) => {
                    const trackName = availableTracks?.find(t => t.id === cut.trackId)?.name || cut.trackId
                    const locomotiveMark = locomotiveReportingMark
                      || availableLocomotives?.find(l => l.id === cut.locomotiveId)?.reportingMark
                      || cut.locomotiveId
                    const carNames = cut.carIds.map(id => railCars?.find(c => c.id === id)?.reportingMark || id).join(', ')
                    
                    return (
                      <div key={cut.passNumber} className="flex items-start justify-between p-2.5 bg-secondary/30 rounded-lg border text-sm">
                        <div>
                          <div className="font-semibold text-foreground">
                            Pass {cut.passNumber}: Move {cut.carIds.length} Rail Cars
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            Destination Track: <span className="font-mono bg-background px-1.5 py-0.5 rounded border">{trackName}</span>
                          </div>
                        </div>
                        <div className="text-right flex flex-col items-end">
                          <span className="text-xs font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full border border-primary/20">
                            Loco: {locomotiveMark || 'Unassigned'}
                          </span>
                          <span className="text-xs text-muted-foreground mt-1 font-mono">
                            {carNames}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={isDispatching}
              className="px-4 py-2 pointer-coarse:min-h-11 border border-border rounded-md text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedCrew || isDispatching || generationErrors.length > 0 || dispatchSubmissionErrors.length > 0}
              className="px-4 py-2 pointer-coarse:min-h-11 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDispatching ? 'Dispatching...' : 'Confirm & Dispatch Work Order'}
            </button>
          </div>
        </div>
      </div>

      {animateOpen && switchList && facilityId && (() => {
        // Prefer the operator's picked loco; the cut's assigned loco is
        // the fallback when picker state is empty (e.g. dispatch reopened
        // from a work order). A difference is advisory, never silent.
        const cutLocoId = plannedCuts?.[0]?.locomotiveId
        const locoNotices: string[] = []
        if (selectedLocomotiveId && cutLocoId && selectedLocomotiveId !== cutLocoId) {
          const mark = (id: string) =>
            availableLocomotives?.find(l => l.id === id)?.reportingMark ?? id
          locoNotices.push(
            `Preview uses the selected locomotive ${mark(selectedLocomotiveId)} — ` +
            `the cut was planned with ${mark(cutLocoId)}`
          )
        }
        return (
          <SwitchListPreviewMapModal
            switchList={switchList}
            facilityId={facilityId}
            locomotiveId={selectedLocomotiveId || cutLocoId}
            railCars={railCars}
            extraNotices={locoNotices}
            onClose={() => setAnimateOpen(false)}
          />
        )
      })()}
    </div>
  )
}
